/**
 * SOLID Review
 * S — Strong. One concern: the tab's guest identity in storage — read (with the legacy migration), saved, cleared.
 * O — Good. A new legacy location is one more read in the migration.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Three methods.
 * D — Strong. The storage areas are handed in.
 */
import type { IConfigDepBrowserStorages } from './IServiceBrowserStorage';

const GUEST_ID_KEY = 'meyerlive3d_guest_id';
const GUEST_NAME_KEY = 'meyerlive3d_guest_name';

interface IEntityStoredGuest {
  readonly id: string | null;
  readonly name: string | null;
}

/**
 * THE GUEST IDENTITY'S STORAGE (frontend-infrastructure-oo P1, 2026-10-28, §28.286) — `guestIdentityStorage.ts`'s functions as
 * one object over the storage areas it is handed, built once by the composition root. Behaviour unchanged.
 *
 * Its one consumer is `ClientTabStorage` (a returning guest, a new one, a rejoin that adopts another seat's id, and a sign-in or
 * sign-out that forgets it). Guest identity is kept PER-TAB but DISCARD-DURABLE in the tab's own area, mirroring the JWT: every
 * new tab starts at the welcome screen (no silent inheritance across tabs), while a backgrounded guest tab the OS discards comes
 * back as the SAME guest instead of being wiped to anonymous. A guest an older build kept in the session's area migrates on read.
 */
export class GuestIdentityStore {
  constructor(private readonly storages: IConfigDepBrowserStorages) {}

  read(): IEntityStoredGuest {
    const { tab, session } = this.storages;
    try {
      let id = tab.getItem(GUEST_ID_KEY);
      let name = tab.getItem(GUEST_NAME_KEY);
      if (!id || !name) {
        const legacyId = session.getItem(GUEST_ID_KEY);
        const legacyName = session.getItem(GUEST_NAME_KEY);
        if (legacyId && legacyName) {
          tab.setItem(GUEST_ID_KEY, legacyId);
          tab.setItem(GUEST_NAME_KEY, legacyName);
          session.removeItem(GUEST_ID_KEY);
          session.removeItem(GUEST_NAME_KEY);
          id = legacyId;
          name = legacyName;
        }
      }
      const guest: IEntityStoredGuest = { id, name };
      return guest;
    } catch {
      const none: IEntityStoredGuest = { id: null, name: null };
      return none;
    }
  }

  save(playerId: string, displayName: string): void {
    const { tab, session } = this.storages;
    try {
      tab.setItem(GUEST_ID_KEY, playerId);
      tab.setItem(GUEST_NAME_KEY, displayName);
      session.removeItem(GUEST_ID_KEY); // drop any legacy per-tab copy
      session.removeItem(GUEST_NAME_KEY);
    } catch { /* ignore */ }
  }

  clear(): void {
    const { tab, session, local } = this.storages;
    try {
      tab.removeItem(GUEST_ID_KEY);
      tab.removeItem(GUEST_NAME_KEY);
      // Also wipe any legacy per-tab / shared copies.
      session.removeItem(GUEST_ID_KEY);
      session.removeItem(GUEST_NAME_KEY);
      local.removeItem(GUEST_ID_KEY);
      local.removeItem(GUEST_NAME_KEY);
    } catch { /* ignore */ }
  }
}
